import { useEffect, useMemo, useState } from 'react'
import type { TourExperienceStop } from '@/lib/types/tours.types'
import {
  drivingRouteCacheKey,
  fetchMapboxDrivingRoute,
  readCachedDrivingRoute,
  type LngLat,
} from '@/lib/maps/mapbox-directions'
import { readRouteCoordinates } from './tour-route-geometry'

/**
 * A line on a Tours map is a commitment: it must follow roads or not exist.
 * A straight segment is useful to a cartographer, but it tells a traveller the
 * wrong thing across mountains, lakes and one-way streets.
 */
export type TourDrivingRouteStatus = 'road' | 'loading' | 'unavailable'

export function useTourDrivingRoute(
  stops: TourExperienceStop[],
  providedRoute?: unknown,
): {
  routeGeojson: unknown
  status: TourDrivingRouteStatus
  isLoading: boolean
} {
  const providedLine = useMemo(() => readRouteCoordinates(providedRoute), [providedRoute])
  const hasProvidedRoadRoute = providedLine.length >= 2
  const waypoints = useMemo(
    () =>
      stops
        .filter(
          (stop) =>
            Number.isFinite(stop.latitude) &&
            Number.isFinite(stop.longitude) &&
            Math.abs(stop.latitude) <= 90 &&
            Math.abs(stop.longitude) <= 180,
        )
        .slice()
        .sort((a, b) => a.position - b.position)
        .map((stop): LngLat => [stop.longitude, stop.latitude]),
    [stops],
  )

  const waypointKey = useMemo(
    () => (waypoints.length >= 2 ? drivingRouteCacheKey(waypoints) : null),
    [waypoints],
  )
  const cached =
    !hasProvidedRoadRoute && waypoints.length >= 2 ? readCachedDrivingRoute(waypoints) : null
  const [fetched, setFetched] = useState<{
    key: string
    route: GeoJSON.Feature<GeoJSON.LineString>
  } | null>(null)
  const [failedKey, setFailedKey] = useState<string | null>(null)

  useEffect(() => {
    if (hasProvidedRoadRoute || !waypointKey || waypoints.length < 2) return
    const already = readCachedDrivingRoute(waypoints)
    if (already) {
      setFetched({ key: waypointKey, route: already })
      setFailedKey((current) => (current === waypointKey ? null : current))
      return
    }
    let cancelled = false
    setFailedKey((current) => (current === waypointKey ? null : current))
    void fetchMapboxDrivingRoute(waypoints).then((feature) => {
      if (cancelled) return
      if (feature) {
        setFetched({ key: waypointKey, route: feature })
        setFailedKey((current) => (current === waypointKey ? null : current))
        return
      }
      setFetched((current) => (current?.key === waypointKey ? null : current))
      setFailedKey(waypointKey)
    })
    return () => {
      cancelled = true
    }
  }, [hasProvidedRoadRoute, waypointKey, waypoints])

  if (hasProvidedRoadRoute) {
    return { routeGeojson: providedRoute, status: 'road', isLoading: false }
  }

  const fetchedForCurrentRoute = fetched?.key === waypointKey ? fetched.route : null
  const roadRoute = cached ?? fetchedForCurrentRoute
  if (roadRoute) {
    return { routeGeojson: roadRoute, status: 'road', isLoading: false }
  }

  if (!waypointKey || failedKey === waypointKey) {
    return { routeGeojson: undefined, status: 'unavailable', isLoading: false }
  }

  return { routeGeojson: undefined, status: 'loading', isLoading: true }
}
